import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ADMIN_TYPE, CRM_SKIP_ALLOWED, DISTRIBUTOR } from '../../../constants/users';
import { editUserRequest } from '../../../store/actions/users';
import { selectCurrentOrganizationData } from '../../../store/selectors/organizations';
import { NotificationListEnum } from '../../../types/shell';
import { getFullName } from '../../../utils/helpers';
import notification from '../../../utils/notification';
import { handleApiError } from '../../../utils/ui';

interface IProps<T> {
  onEdit?: (item: T) => void;
  onDelete?: (id: string) => void;
  canEdit?: (item: T) => boolean;
}

export const useUsersContextMenu = <T extends { uid: string; email: string }>({
  onEdit,
  onDelete,
  canEdit = () => true,
}: IProps<T>) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<T | null>(null);

  const { options: organizationOptions } = useSelector(selectCurrentOrganizationData) || {};
  const isSkipCrmAllowedInOrg = organizationOptions?.crm_skip_allowed;
  const dispatch = useDispatch();

  const handleMenuOpen = useCallback((event: React.MouseEvent<Element>, item: T) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget as HTMLElement);
    setSelectedItem(item);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    setSelectedItem(null);
  }, []);

  const handleEdit = useCallback(() => {
    if (selectedItem && canEdit(selectedItem) && onEdit) {
      onEdit(selectedItem);
    }
  }, [canEdit, onEdit, selectedItem]);

  const handleDelete = useCallback(() => {
    if (selectedItem && onDelete) {
      onDelete(selectedItem.uid);
    }
  }, [onDelete, selectedItem]);

  const handleSkipCrm = useCallback(() => {
    if (selectedItem && canEdit(selectedItem)) {
      const { distributor_org_ids, crm_skip_allowed, ...rest } = selectedItem as any;
      const newState = !crm_skip_allowed;

      setSelectedItem({
        ...selectedItem,
        [CRM_SKIP_ALLOWED]: newState,
      });

      const candidate = {
        ...(rest[ADMIN_TYPE] === DISTRIBUTOR ? { distributor_org_ids } : {}),
        crm_skip_allowed: newState,
        ...rest,
      };

      return new Promise((resolve, reject) => {
        dispatch(
          editUserRequest(
            {
              user: candidate,
              resolve: () => {
                resolve(true);
                notification.success(NotificationListEnum.Success, {
                  content: `Skip CRM ${newState ? 'enabled' : 'disabled'} for ${getFullName(
                    candidate.first_name,
                    candidate.last_name,
                  )}.`,
                });
                handleMenuClose();
              },
              reject: () => {
                setSelectedItem({
                  ...selectedItem,
                  [CRM_SKIP_ALLOWED]: crm_skip_allowed,
                });
                reject();
                handleApiError(`Failed to update Skip CRM.`);
                handleMenuClose();
              },
            },
            { isSilent: true },
          ),
        );
      });
    }
  }, [dispatch, selectedItem, canEdit]);

  return {
    menuAnchorEl,
    selectedItem,
    handleMenuOpen,
    handleMenuClose,
    handleEdit,
    handleDelete,
    handleSkipCrm,
    isSkipCrmAllowedInOrg,
    canEdit,
  };
};
